import React from "react";

const ExtensionsDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          ApplicationBuilderExtensions — Middleware Pipeline Configuration
        </h3>

        <p className="mb-4">
          The <strong>ApplicationBuilderExtensions</strong> class defines the
          runtime pipeline-level middleware configuration required for all
          AxisNet-based APIs. These extensions must be executed in{" "}
          <code>Program.cs</code> to ensure health monitoring, secure headers,
          structured exception handling, and standardized operational behavior.
        </p>

        <h4 className="text-xl text-white font-semibold mb-3 mt-6">
          UseAxisNetHealth — Health Check Endpoints
        </h4>

        <p className="mb-4">
          Registers the <strong>/health</strong>, <strong>/health-ui</strong>,
          and <strong>/health-json</strong> endpoints. These endpoints expose
          system health, database availability, Redis connectivity, and
          infrastructure diagnostics in both JSON and UI format.
        </p>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>/health</strong> — machine-readable status for automated
            monitoring.
          </li>
          <li>
            <strong>/health-ui</strong> — interactive dashboard for system
            health.
          </li>
          <li>
            <strong>/health-json</strong> — JSON interface consumed by the UI.
          </li>
        </ul>

        <h5 className="text-lg text-emerald-300 font-semibold mt-4 mb-2">
          Example
        </h5>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300 whitespace-pre-wrap">{`app.UseAxisNetHealth();`}</pre>

        <h4 className="text-xl text-white font-semibold mb-3 mt-10">
          UseAxisNetGlobalExceptionMiddleware — Unified Exception Pipeline
        </h4>

        <p className="mb-4">
          Enables the centralized <strong>GlobalExceptionMiddleware</strong>,
          which intercepts, formats, and logs all unhandled exceptions. It also
          sends developer email notifications when a critical error occurs.
        </p>

        <p className="mb-3">Required configuration:</p>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">{`"Developer": { "Mail": "your-email@example.com" }`}</pre>

        <h5 className="text-lg text-emerald-300 font-semibold mt-4 mb-2">
          Example
        </h5>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300 whitespace-pre-wrap">{`app.UseAxisNetGlobalExceptionMiddleware(builder.Configuration);`}</pre>

        <h4 className="text-xl text-white font-semibold mb-3 mt-10">
          UseAxisNetSecureHeaderMiddleware — Security Hardening Layer
        </h4>

        <p className="mb-4">
          Adds browser security headers protecting the API from clickjacking,
          MIME sniffing, and XSS. Recommended for all production environments.
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>X-Frame-Options</li>
          <li>X-Content-Type-Options</li>
          <li>Referrer-Policy</li>
          <li>X-XSS-Protection</li>
          <li>Content-Security-Policy</li>
        </ul>

        <h5 className="text-lg text-emerald-300 font-semibold mt-4 mb-2">
          Example
        </h5>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300 whitespace-pre-wrap">{`app.UseAxisNetSecureHeaderMiddleware();`}</pre>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          CrudOperationCollectionExtensions — Repository & Service Binding
        </h3>

        <p className="mb-4">
          These extensions register all CRUD-related services and repositories
          necessary for authentication, authorization, verification code
          handling, sessions, and user/role/permission management.
        </p>

        <h4 className="text-lg text-white font-semibold mb-3">
          AddAxisNetServices
        </h4>

        <p className="mb-4">
          Registers core application business logic services, such as:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Authentication Service</li>
          <li>Verification Code Service</li>
        </ul>

        <h4 className="text-lg text-white font-semibold mb-3 mt-6">
          AddAxisNetRepository
        </h4>

        <p className="mb-4">
          Registers repository implementations for all domain entities:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Users</li>
          <li>Roles</li>
          <li>Permissions</li>
          <li>UserRoles</li>
          <li>UserPermissions</li>
          <li>Logs</li>
          <li>Sessions</li>
          <li>Verification Codes</li>
        </ul>

        <h5 className="text-lg text-emerald-300 font-semibold mt-4 mb-2">
          Example
        </h5>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">{`services.AddAxisNetRepository<AppDbContext>();
services.AddAxisNetServices();`}</pre>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          ProgramCollectionExtensions — CORS, Swagger & Versioning
        </h3>

        <p className="mb-4">
          This group defines startup-level configuration helpers for CORS
          policies, Swagger generation, and API versioning.
        </p>

        <h4 className="text-lg text-white font-semibold mb-3">
          AddAxisNetCorsPolicy
        </h4>
        <p className="mb-3">
          Allows the API to specify trusted front-end origins and enables
          headers, credentials, and all HTTP methods.
        </p>

        <h5 className="text-emerald-300 font-semibold mb-2">Example</h5>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">{`services.AddAxisNetCorsPolicy("Frontend", "https://example.com");`}</pre>

        <h4 className="text-lg text-white font-semibold mb-3 mt-6">
          AddAxisNetSwagger
        </h4>

        <p className="mb-3">
          Registers Swagger UI with JWT authentication support.
        </p>

        <h5 className="text-emerald-300 font-semibold mb-2">Example</h5>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">{`services.AddAxisNetSwagger("MyProject");`}</pre>

        <h4 className="text-lg text-white font-semibold mb-3 mt-6">
          AddAxisNetVersioning
        </h4>

        <p className="mb-3">
          Enables URL-based API versioning (e.g., /api/v1/...)
        </p>

        <h5 className="text-emerald-300 font-semibold mb-2">Example</h5>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">{`services.AddAxisNetVersioning(1, 0);`}</pre>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10 mb-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          ServiceCollectionExtensions — AxisNet Core Service Configuration
        </h3>

        <p className="mb-4">
          This is the most critical extension group. It configures behaviors,
          JWT authentication, Redis caching, localization, HTML sanitization,
          business rule scanning, client info parsing, geolocation, Recaptcha
          validation, and all MediatR pipeline behaviors.
        </p>

        <h4 className="text-lg text-white font-semibold mb-3">
          AddAxisNetApplication
        </h4>
        <p className="mb-3">
          Automatically loads assemblies, validators, AutoMapper profiles, and
          registers all MediatR handlers.
        </p>

        <h4 className="text-lg text-white font-semibold mb-3 mt-6">
          AddMediatorWithBehaviors
        </h4>

        <p className="mb-3">Registers all MediatR pipeline behaviors:</p>

        <ul className="list-disc pl-6 space-y-1">
          <li>AuthorizationBehavior</li>
          <li>SessionValidationBehavior</li>
          <li>LocalizationBehavior</li>
          <li>ValidationBehavior</li>
          <li>RecaptchaBehavior</li>
          <li>XssPreventBehavior</li>
          <li>LogBehavior</li>
          <li>RedisCacheBehavior</li>
          <li>TransactionalBehavior</li>
        </ul>

        <h5 className="text-emerald-300 font-semibold mb-2 mt-4">Example</h5>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">{`services.AddMediatorWithBehaviors(typeof(Program).Assembly);`}</pre>

        <h4 className="text-lg text-white font-semibold mt-6 mb-3">
          AddAxisNetJwtService
        </h4>

        <p className="mb-3">
          Adds JWT Bearer authentication + token generation service.
        </p>

        <h4 className="text-lg text-white font-semibold mt-6 mb-3">
          AddAxisNetRedisCache
        </h4>

        <p className="mb-3">
          Registers Redis distributed cache + IRedisCacheService.
        </p>

        <h4 className="text-lg text-white font-semibold mt-6 mb-3">
          AddAxisNetLocalization
        </h4>

        <p className="mb-3">
          Enables dynamic per-request culture switching (Accept-Language).
        </p>

        <h4 className="text-lg text-white font-semibold mt-6 mb-3">
          AddAxisNetHtmlSanitizer
        </h4>

        <p className="mb-3">
          Registers HTML sanitization for XSS-safe input cleaning.
        </p>
      </div>
    </section>
  );
};

export default ExtensionsDoc;
